import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight, CornerDownLeft, FileClock, FlaskConical, Radio, Search, Tag } from "lucide-react";
import type { OrderRecord } from "@/api/types";
import { useApi } from "../context/ApiContext";
import { buildSearchItems, searchItems, type SearchItem } from "@/lib/search";
import { useTimezoneStore } from "@/stores/timezone";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

function ItemIcon({ item }: { item: SearchItem }) {
  const cls = "shrink-0 text-[var(--text-muted)]";
  if (item.group === "Strategies")
    return item.hint?.startsWith("Live") ? <Radio size={15} className={cls} /> : <FlaskConical size={15} className={cls} />;
  if (item.group === "Symbols") return <Tag size={15} className={cls} />;
  if (item.group === "Accounts") return <ArrowLeftRight size={15} className={cls} />;
  if (item.group === "Orders") return <FileClock size={15} className={cls} />;
  return <CornerDownLeft size={15} className={cls} />;
}

/** Top-bar search field + ⌘K palette over strategies, symbols, exchanges, orders and pages. */
export default function GlobalSearch() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const api = useApi(open ? ["accounts", "backtests"] : []);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const ordersLoaded = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const tzMode = useTimezoneStore((st) => st.mode);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    returnFocus.current = document.activeElement as HTMLElement | null;
    setQuery("");
    setActive(0);
    requestAnimationFrame(() => inputRef.current?.focus());
    // Orders aren't synced like strategies; fetch the latest page once per session.
    if (!ordersLoaded.current) {
      ordersLoaded.current = true;
      api
        .commands.listOrders({ account_id: "", backtest_id: "", status: "", limit: 200 })
        .then((rows: OrderRecord[] | null) => setOrders(Array.isArray(rows) ? rows : []))
        .catch(() => {
          ordersLoaded.current = false;
        });
    }
    return () => returnFocus.current?.focus();
    // `api` changes identity on every sync message; only react to opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const items = useMemo(
    () =>
      buildSearchItems({
        backtests: api.backtests || [],
        accounts: api.accounts || [],
        orders: orders,
        tzMode,
      }),
    [api.backtests, api.accounts, orders, tzMode]
  );

  const recentIds = useMemo(
    () =>
      [...(api.backtests || [])]
        .sort((a, b) => Date.parse(b.updated_at || "") - Date.parse(a.updated_at || ""))
        .slice(0, 4)
        .map((bt) => `bt-${bt.id}`),
    [api.backtests]
  );

  const results = useMemo(() => searchItems(items, query, recentIds), [items, query, recentIds]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  function go(item: SearchItem | undefined) {
    if (!item) return;
    setOpen(false);
    nav(item.to);
  }

  function onInputKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  let lastGroup = "";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search strategies, symbols, orders"
        className="flex h-11 w-11 items-center justify-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)] text-xs text-[var(--text-disabled)] hover:border-[var(--border-hover)] hover:text-[var(--text-muted)] md:h-auto md:w-auto md:justify-start md:px-2.5 md:py-[5px]"
      >
        <Search size={14} className="shrink-0" />
        <span className="hidden truncate text-left md:inline">Search strategies, symbols, orders…</span>
        <kbd className="num hidden rounded border border-[var(--border)] px-1 text-[10px] md:inline">
          {isMac ? "⌘K" : "Ctrl K"}
        </kbd>
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 px-4 pt-[12vh]"
            onMouseDown={() => setOpen(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Search"
              className="flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-dialog border border-[var(--border)] bg-[var(--bg-card)] shadow-lg"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2.5 border-b border-[var(--border)] px-4">
                <Search size={16} className="shrink-0 text-[var(--text-muted)]" />
                <input
                  ref={inputRef}
                  id="global-search-input"
                  role="combobox"
                  aria-expanded
                  aria-controls="global-search-results"
                  aria-activedescendant={results[active] ? `gs-${results[active].id}` : undefined}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onInputKey}
                  placeholder="Search strategies, symbols, exchanges, orders…"
                  className="h-12 flex-1 !border-0 !bg-transparent !px-0 text-sm text-[var(--text-primary)] !shadow-none !outline-none focus:!ring-0"
                />
                <kbd className="rounded border border-[var(--border)] px-1.5 font-mono text-[10px] text-[var(--text-muted)]">Esc</kbd>
              </div>

              <div ref={listRef} id="global-search-results" role="listbox" className="overflow-y-auto py-1.5">
                {results.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-[var(--text-muted)]">
                    Nothing matches “{query}”.
                  </div>
                ) : (
                  results.map((item, idx) => {
                    const heading =
                      !query.trim() && item.group === "Strategies" ? "Recent" : item.group;
                    const showHeading = heading !== lastGroup;
                    lastGroup = heading;
                    return (
                      <div key={item.id}>
                        {showHeading && (
                          <div className="px-4 pb-1 pt-2.5 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--text-muted)]">
                            {heading}
                          </div>
                        )}
                        <div
                          id={`gs-${item.id}`}
                          role="option"
                          aria-selected={idx === active}
                          data-idx={idx}
                          className={`mx-1.5 flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm ${
                            idx === active ? "bg-[var(--bg-hover)]" : ""
                          }`}
                          onMouseMove={() => setActive(idx)}
                          onClick={() => go(item)}
                        >
                          <ItemIcon item={item} />
                          <span className="min-w-0 flex-1 truncate text-[var(--text-primary)]">{item.label}</span>
                          {item.hint && (
                            <span className="shrink-0 truncate text-xs text-[var(--text-muted)]">{item.hint}</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="flex items-center gap-4 border-t border-[var(--border)] px-4 py-2 text-[11px] text-[var(--text-muted)]">
                <span>↑↓ to move</span>
                <span>↵ to open</span>
                <span className="ml-auto">Orders: latest 200</span>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
